import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Heart, Loader2, Lock, MapPin, Phone, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { ErrorState } from '@/components/ui/error-state';
import { ImageCarousel } from '@/components/image-carousel';
import { form_error_message } from '@/lib/forms';
import { use_session_store } from '@/lib/session-store';
import { open_razorpay_checkout } from '@/features/booking/razorpay-checkout';
import { use_browse_detail, use_create_request, use_pay_request, use_verify_request_payment } from '@/features/adoption-listing/hooks';
import { ADOPTION_FEE_INR, SPECIES_LABEL, age_label } from '@/features/adoption-listing/types';

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

export function AdoptListingDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const phone = use_session_store((s) => s.phone) ?? undefined;
  const detail_q = use_browse_detail(id);
  const create_m = use_create_request();
  const pay_m = use_pay_request();
  const verify_m = use_verify_request_payment();
  const [error, set_error] = useState<string | null>(null);

  const l = detail_q.data;
  const unlocked = !!l?.contact;
  const paying = create_m.isPending || pay_m.isPending || verify_m.isPending;

  async function adopt() {
    if (!id) return;
    set_error(null);
    try {
      const req = await create_m.mutateAsync(id);
      if (req.checkout) {
        await open_razorpay_checkout({
          checkout: req.checkout,
          service_name: 'Adoption platform fee',
          contact: phone,
          on_success: async (resp) => {
            try {
              await verify_m.mutateAsync({
                id: req.id,
                input: { razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature },
              });
              await detail_q.refetch();
            } catch (err) {
              set_error(form_error_message(err, 'Payment verification failed. If charged, contact support.'));
            }
          },
          on_dismiss: () => set_error('Payment cancelled. You can try again.'),
          on_failure: (message) => set_error(message),
        });
      } else {
        await pay_m.mutateAsync(req.id);
        await detail_q.refetch();
      }
    } catch (err) {
      set_error(form_error_message(err, 'Could not start the adoption. Please try again.'));
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
      <Button variant="ghost" size="sm" className="self-start" onClick={() => navigate('/app/adopt')}>
        <ArrowLeft className="h-4 w-4" /> Back to Adopt
      </Button>

      {detail_q.isLoading ? (
        <div className="flex items-center justify-center p-12 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
      ) : detail_q.isError || !l ? (
        <ErrorState data-testid="adopt-detail-error" title="Listing unavailable" description="This pet may have already found a home." />
      ) : (
        <>
          {l.photos.length > 0 ? (
            <ImageCarousel images={l.photos.map((p) => ({ id: p.id, thumb_path: `${p.file_path}?variant=thumb`, full_path: `${p.file_path}?variant=full` }))} cover />
          ) : (
            <div className="flex aspect-[16/10] w-full items-center justify-center rounded-card accent-gradient text-white/90"><Heart className="h-12 w-12" /></div>
          )}

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl font-bold">{SPECIES_LABEL[l.species]}{l.breed ? ` · ${l.breed}` : ''}</h1>
              {l.status === 'ADOPTED' && <StatusPill tone="info">Adopted</StatusPill>}
            </div>
            <p className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-4 w-4" /> {[l.city, l.state].filter(Boolean).join(', ')}</p>
          </div>

          <Card>
            <CardContent className="flex flex-col p-5">
              <Detail label="Age" value={age_label(l)} />
              <Detail label="Gender" value={l.gender !== 'UNKNOWN' ? l.gender.toLowerCase() : null} />
              <Detail label="Vaccinated" value={l.vaccinated == null ? null : l.vaccinated ? 'Yes' : 'No'} />
              <Detail label="Sterilised" value={l.sterilized == null ? null : l.sterilized ? 'Yes' : 'No'} />
              <Detail label="Health" value={l.health_status} />
              <Detail label="Good with" value={l.good_with} />
            </CardContent>
          </Card>

          {l.description && (
            <Card><CardContent className="p-5"><p className="whitespace-pre-wrap text-sm">{l.description}</p></CardContent></Card>
          )}

          {/* Contact — gated behind the platform fee */}
          {unlocked ? (
            <Card data-testid="adopt-contact-card">
              <CardContent className="flex flex-col gap-2 p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-success"><ShieldCheck className="h-4 w-4" /> Contact unlocked</p>
                <Detail label="Name" value={l.contact!.contact_name} />
                <div className="flex justify-between gap-3 py-2 text-sm">
                  <span className="text-muted-foreground">Phone</span>
                  <a href={`tel:${l.contact!.contact_phone}`} className="flex items-center gap-1 font-medium text-primary hover:underline" data-testid="adopt-contact-phone">
                    <Phone className="h-3.5 w-3.5" /> {l.contact!.contact_phone}
                  </a>
                </div>
                <Detail label="Address" value={l.contact!.address_line} />
                <Detail label="Pincode" value={l.contact!.pincode} />
              </CardContent>
            </Card>
          ) : (
            <Card data-testid="adopt-unlock-card">
              <CardContent className="flex flex-col gap-3 p-5">
                <p className="flex items-center gap-2 text-sm font-semibold"><Lock className="h-4 w-4 text-muted-foreground" /> Contact details are hidden</p>
                <p className="text-xs text-muted-foreground">
                  Pay a one-time <span className="font-semibold text-foreground">₹{ADOPTION_FEE_INR} platform service fee</span> to reveal the poster's name, phone &amp; address.
                  This fee supports the platform and is <span className="font-semibold text-foreground">not paid to the poster</span>.
                </p>
                {error && <p className="text-sm text-destructive" data-testid="adopt-pay-error">{error}</p>}
                <div>
                  <Button variant="gradient" onClick={adopt} disabled={paying || l.status !== 'PUBLISHED'} data-testid="adopt-unlock-cta">
                    {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
                    Adopt · reveal contact (₹{ADOPTION_FEE_INR})
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
